import { doc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase';

// Save user's selected icons
export const saveUserIcons = async (userId, icons) => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      'profile.icons': icons
    });
    return true;
  } catch (error) {
    console.error('Error saving icons:', error);
    throw error;
  }
};

// Save user's links
export const saveUserLinks = async (userId, links) => {
  try {
    const userRef = doc(db, 'users', userId);
    // Make sure to preserve all fields including imageUrl
    const processedLinks = links.map(link => ({
      id: link.id,
      title: link.title,
      url: link.url,
      hidden: link.hidden || false,
      ghostProtected: link.ghostProtected || false,
      ghostPassword: link.ghostPassword || null,
      ghostPasswordHash: link.ghostPasswordHash || null,
      pinned: link.pinned || false,
      order: link.order || 0,
      imageUrl: link.imageUrl || null // Preserve imageUrl
    }));
    
    await updateDoc(userRef, {
      'profile.links': processedLinks
    });
    return true;
  } catch (error) {
    console.error('Error saving links:', error);
    throw error;
  }
};

// Save user's selected palette
export const saveUserPalette = async (userId, palette) => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      'profile.palette': palette
    });
    return true;
  } catch (error) {
    console.error('Error saving palette:', error);
    throw error;
  }
};

// Save user's bio
export const saveUserBio = async (userId, bio) => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      'profile.bio': bio
    });
    return true;
  } catch (error) {
    console.error('Error saving bio:', error);
    throw error;
  }
};

// Upload and save profile picture
export const uploadProfilePicture = async (userId, file) => {
  try {
    // Create storage reference
    const storageRef = ref(storage, `profile-pics/${userId}`);
    
    // Upload file
    await uploadBytes(storageRef, file);
    
    // Get download URL
    const photoURL = await getDownloadURL(storageRef);
    
    // Update user document with new photo URL
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      photoURL: photoURL
    });
    
    return photoURL;
  } catch (error) {
    console.error('Error uploading profile picture:', error);
    throw error;
  }
};

// Upload link image and return the URL
export const uploadLinkImage = async (userId, linkId, file) => {
  try {
    // Create a unique file path with timestamp to prevent caching issues
    const fileExtension = file.name.split('.').pop();
    const fileName = `link-images/${userId}_${linkId}_${Date.now()}.${fileExtension}`;
    const imageRef = ref(storage, fileName);
    
    // Upload the file
    const snapshot = await uploadBytes(imageRef, file);
    
    // Get download URL
    const imageUrl = await getDownloadURL(snapshot.ref);
    
    return imageUrl;
  } catch (error) {
    console.error('Error uploading link image:', error);
    throw error;
  }
};

// Save banner data directly to Firestore - FIXED
export const updateUserBanner = async (userId, bannerData) => {
  try {
    if (!userId) {
      console.error('Cannot update banner: Missing user ID');
      return false;
    }
    
    // Clean any temporary flags before saving to database
    let cleanedBannerData = null;
    
    if (bannerData) {
      // Make a deep copy of the banner data first to avoid reference issues
      const bannerCopy = JSON.parse(JSON.stringify(bannerData));
      
      // Remove all temporary flags and file object (if any)
      const { preventEditingStateChange, disableAutoSave, _manualChange, file, ...cleanData } = bannerCopy;
      
      // Only use cleaned data for database
      cleanedBannerData = cleanData;
      
      console.log('Cleaned banner data for database:', cleanedBannerData);
    }
    
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      'profile.banner': cleanedBannerData
    });
    
    console.log('Banner data saved successfully to Firestore');
    return true;
  } catch (error) {
    console.error('Error saving banner data:', error);
    throw error;
  }
};

// Upload banner image and save to user profile - FIXED
export const uploadBannerImage = async (userId, file, position = 50) => {
  try {
    if (!userId || !file) {
      console.error('Cannot upload banner: Missing user ID or file');
      return null;
    }
    
    // Create a unique file path
    const fileExtension = file.name.split('.').pop() || 'jpg';
    const fileName = `banners/${userId}_${Date.now()}.${fileExtension}`;
    const storageRef = ref(storage, fileName);
    
    console.log('Uploading banner to storage path:', fileName);
    
    // Upload the file
    const uploadResult = await uploadBytes(storageRef, file);
    
    // Get download URL
    const downloadURL = await getDownloadURL(uploadResult.ref);
    
    // Create banner data object
    const bannerData = {
      url: downloadURL,
      position: position,
      storagePath: fileName,
      isDefault: false
    };
    
    console.log('Banner uploaded successfully, saving to user profile:', bannerData);
    
    // Save to user profile
    await updateUserBanner(userId, bannerData);
    
    return bannerData;
  } catch (error) {
    console.error('Error uploading banner image:', error);
    throw error;
  }
};

// Delete banner image from storage
export const deleteBannerImage = async (storagePath) => {
  if (!storagePath) return true;
  
  try {
    console.log('Deleting banner from storage path:', storagePath);
    const storageReference = ref(storage, storagePath);
    await deleteObject(storageReference);
    console.log('Banner image deleted successfully');
    return true;
  } catch (error) {
    console.error('Error deleting banner from storage:', error);
    return false;
  }
};